import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { MIDirectivesModule } from 'src/app/directives/directives.module';
import { AppTranslationService } from 'src/app/services/app-translation.service';
import { AUTH_UI_SERVICE, AuthDialogPage, AuthUIServiceInterface } from 'src/app/services/auth/auth-ui.service.interface';
import { ConfirmDialogData } from 'src/app/services/dialogs/dialogs.ui.interface';
import { DialogsUIService } from 'src/app/services/dialogs/dialogs.ui.service';
import { LinkOpenerService } from 'src/app/services/link-opener.service';
import { TRACKING_SERVICE, TrackingService } from 'src/app/services/tracking/tracking.model';
import { UiUtilsService } from 'src/app/services/utils/ui-utils.service';
import { AuthProvider } from 'src/app/services/yeti-protocol/auth/provider';
import { ActionSource, ActionTracked, SignInSignUpTrackingParam, TrackingRequest } from 'src/app/services/yeti-protocol/tracking';
import appConfig from 'src/config/config';

export enum ButtonType {
  signin = 'signIn',
  signup = 'signUp'
}

@Component({
  selector: 'app-auth-buttons',
  templateUrl: './auth-buttons.component.html',
  styleUrls: ['./auth-buttons.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    MIDirectivesModule
  ]
})
export class AuthButtonsComponent implements OnInit {

  @Input() showHelpCenterLink: boolean;
  @Input() titlePart1: string;
  @Input() titlePart2: string;
  @Input() subtitle: string;
  @Input() source: ActionSource = ActionSource.unspecified;
  @Input() disableButtonsAndShowSpinner: boolean;

  AuthProvider = AuthProvider;
  hasAO = false;
  dialogOpened = false;

  constructor(
    @Inject(AUTH_UI_SERVICE) private authUIService: AuthUIServiceInterface,
    private linkOpenerService: LinkOpenerService,
    @Inject(TRACKING_SERVICE) private trackingService: TrackingService,
    private appTranslationService: AppTranslationService,
    private uiUtilsService: UiUtilsService,
    private dialogs: DialogsUIService
  ) { }

  ngOnInit(): void {
    if (!(this.titlePart1 && this.subtitle)) {
      this.appTranslationService.get([
        'app.components.AuthButtonsComponent.title-part-1',
        'app.components.AuthButtonsComponent.subtitle'
      ]).then(res => {
        if (!this.titlePart1) {
          this.titlePart1 = res['app.components.AuthButtonsComponent.title-part-1'];
        }
        if (!this.subtitle) {
          this.subtitle = res['app.components.AuthButtonsComponent.subtitle'];
        }
      });
    }
    this.hasAO = this.authUIService.availableProviders.includes(AuthProvider.AO);
  }

  async signIn(provider: AuthProvider): Promise<void> {

    if (this.disableButtonsAndShowSpinner) {
      return;
    }

    if (provider === AuthProvider.MI) {
      this.dialogOpened = true;
      this.authUIService.openDialog(AuthDialogPage.SignIn);
      return;
    }

    const actionTracked = ActionTracked.signInStart;

    this.trackSignUpStartAction(provider, this.source, ButtonType.signin, actionTracked);
    this.authUIService.signIn(provider)?.catch(() => {
      this.uiUtilsService.showGenericToastError();
    });
  }

  async signUp(provider: AuthProvider): Promise<void> {

    if (this.disableButtonsAndShowSpinner) {
      return;
    }

    if (provider === AuthProvider.MI) {
      this.dialogOpened = true;
      this.authUIService.openDialog(AuthDialogPage.SignIn);
      return;
    }

    const actionTracked = ActionTracked.signUpStart;

    const shouldProceed = await this.showForcedVerificationConfirmDialog();

    if (!shouldProceed) {
      this.trackSignUpStartAction(provider, this.source, ButtonType.signup, ActionTracked.signUpCancel);
      return;
    }

    this.trackSignUpStartAction(provider, this.source, ButtonType.signup, actionTracked);
    this.authUIService.signUp(provider, null)?.catch(() => {
      this.uiUtilsService.showGenericToastError();
    });
  }

  onOpenHelpCenter(): void {
    this.linkOpenerService.open(appConfig.link.helpCenter);
  }

  onOpenMIsignIn(): void {
    this.signIn(AuthProvider.MI);
  }

  private trackSignUpStartAction(provider, source, buttonType, actionTracked): void {
    const paramsToTrack: SignInSignUpTrackingParam = {
      provider: provider
    };
    if (source && source !== ActionSource.unspecified) {
      paramsToTrack.source = source;
    }

    const trackData: TrackingRequest = {
      action: actionTracked,
      params: paramsToTrack
    };
    this.trackingService.track(trackData)?.catch(_err => {
      console.error('Could not track signup-start action');
    });
  }

  async showForcedVerificationConfirmDialog(): Promise<boolean> {
    const dialogData: ConfirmDialogData = {
      componentProps: {
        title: {
          translationKey: 'app.components.AuthButtonsComponent.force-verification-dialog.title'
        },
        message: {
          translationKey: 'app.components.AuthButtonsComponent.force-verification-dialog.text'
        },
        actions: [
          {
            key: 'exit',
            label: { translationKey: 'app.components.AuthButtonsComponent.force-verification-dialog.btn-cancel' },
            className: 'secondary'
          },
          {
            key: 'continue',
            label: { translationKey: 'app.components.AuthButtonsComponent.force-verification-dialog.btn-confirm' },
            className: 'primary'
          },
        ]
      }
    };

    const confirmModal = await this.dialogs.createConfirmDialog(dialogData);
    confirmModal.present();

    const res = await confirmModal.onDidDismiss();

    if (res && res?.data.actionKey === 'continue') {
      return true;
    }

    return false;

  }

}
